import { createContext, useContext, useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
// Contexts
import { useDatabase } from './DatabaseContext'
import { useAuthInfo } from '@propelauth/react'

const QueryContext = createContext([])

const generateQueryExamples = (orgId, databases) => {
    return [
        {
            id: uuidv4(),
            query_name: 'Query 1',
            query_description: 'Query 1 Description',
            database: databases[0],
            table: 'Experiment',
            query_sql: `SELECT * FROM users;`,
        },
        {
            id: uuidv4(),
            query_name: 'Table 3 Query',
            query_description: 'Table 3 Query Description',
            database: databases[1],
            table: 'RNA_UNIT_OPERATION_SUMMARY',
            query_sql: `SELECT 
  e.id AS experiment_id,
  e.source_id,
  e."schema",
  e."file_registry_id$",
  ruos.unit_operation,
  ruos.reaction_size,
  ruos.reaction_size_units,
  ruos.final_concentration,
  ruos.final_concentration_unit,
  ruos.ramp_start_time,
  ruos.reaction_start_time,
  ruos.dnase_start_time,
  ruos.reaction_end_time,
  ruos.ramp_start_time,
  ruos.reagent_container,
  ruos.step_recovery,
  ruos.total_recovery,
  ruos.actual_ivt_titer as ivt_yield,
  ruos.reaction_volume,
  ruos.reaction_volume_units,
  ruos.reaction_vessel,
  ruos.reagent_container
FROM benchling_stg.experiment e
LEFT JOIN benchling_stg.rna_unit_operation_summary ruos 
  ON e.id = ruos.experiment
  where  e."file_registry_id$" = {{id}};`,
        },
        {
            id: uuidv4(),
            query_name: 'Executive Summary Query',
            query_description: 'Executive Summary Query Description',
            database: databases[2],
            table: 'RNA_EXPERIMENT_SUMMARY',
            query_sql: `SELECT 
  e.id, 
  e.source_id, 
  e."schema", 
  e."file_registry_id$" AS benchling_experiment, 
  e."schema_id$", 
  e."type$",
  s.experiment,
  s.process_id, 
  s.product_id, 
  s.construct,
  s.process_start_date,
  s.process_end_date,
  s."entry_id$",
  s.parent_plasmid,
  p."name$" AS plasmid_name,
  rl.id AS rna_lottable_id,
  rl."name$" AS rna_lottable_name,
  rl."file_registry_id$" AS batch_name,
  rl.name,
  rl.construct,
  c.id AS construct_id,
  c."name$" AS constructname,
  c."file_registry_id$" AS construct_file_name,
  c.sequence_id AS sequence_id,
  seq."name$" AS sequence_name,  -- Added sequence name
  rrfuo.unit_operation,
  rrfuo.step_yield,
  rrfuo.cumulative_yield
FROM benchling_stg.experiment e
LEFT JOIN benchling_stg.rna_experiment_summary s 
  ON e.id = s.experiment
LEFT JOIN benchling_stg.rna_lot rl 
  ON e.id = rl.experiment
LEFT JOIN benchling_stg.construct c 
  ON rl.construct = c.id
LEFT JOIN benchling_stg.sequence seq 
  ON c.sequence_id = seq.id  -- Join on sequence_id
LEFT JOIN benchling_stg.rna_recoveries_for_unit_operations rrfuo 
  ON e.id = rrfuo.experiment
LEFT JOIN benchling_stg.plasmid p
  ON s.parent_plasmid = p.id
WHERE rrfuo.unit_operation = '{{unit_operation}}'
  AND e."file_registry_id$" IN ('{{id}}');`,
        },
        {
            id: uuidv4(),
            query_name: 'Query With Variables',
            query_description: 'Query With Variables Description',
            database: databases[2],
            table: 'Experiment',
            query_sql: `SELECT 
    {{properties}} 
FROM 
    {{TABLE}} 
WHERE id = {{id}};`,
        },
        // {
        //     id: uuidv4(),
        //     query_name: "Manufacturing Site Summary",
        //     query_description: "Summary of manufacturing process scale and site information",
        //     database: databases[2],
        //     table: "manufacturing_sites",
        //     query_sql: "SELECT site, nominal_batch_scale, part_number FROM manufacturing_sites WHERE product_code = 'CX-XXXXXX'"
        // },
        // {
        //     id: uuidv4(),
        //     query_name: "Comparability Lots",
        //     query_description: "CX-XXXXXX mRNA lots used for comparability demonstration",
        //     database: databases[2],
        //     table: "batch_records",
        //     "query_sql": "SELECT lot_number, use_description, site, train FROM batch_records WHERE product_code = 'CX-XXXXXX' AND use_description LIKE '%Comparability%'"
        // }
    ]
}

export function QueryProvider({ children }) {
    const [queries, setQueries] = useState([])
    const { databases } = useDatabase()
    const authInfo = useAuthInfo()
    const orgId = authInfo.orgId

    useEffect(() => {
        console.log(databases)
        const queries = generateQueryExamples(orgId, databases)
        const queriesWithVars = queries.map((query) => {
            const variables = [...query.query_sql.matchAll(/{{(.*?)}}/g)]
            const newQuery = {
                ...query,
                variableList: variables.map((variable) => {
                    return {
                        id: uuidv4(),
                        representation: variable[0],
                        label: variable[1],
                        value: [],
                    }
                }),
            }
            return newQuery
        })
        console.log(queriesWithVars)
        setQueries(queriesWithVars)
    }, [databases])

    const value = {
        queries,
        setQueries
    }

    return (
        <QueryContext.Provider value={value}>
            {children}
        </QueryContext.Provider>
    )
}

export function useQuery() {
    const context = useContext(QueryContext)
    if (!context) {
        throw new Error('useQuery must be used within a QueryProvider')
    }   
    return context
}
